import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Label from "../../../components/BaseUI/Label";
import { FiHardDrive, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import { formatInstantDateShortTr } from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ENTRY_TYPE_OPTIONS = [
  { label: "Input", value: "input" },
  { label: "OPC", value: "opc" },
  { label: "PLC Modbus", value: "plc_modbus" },
];
const DEFAULT_ENTRY_TYPE = "input";

const RELAY_OPTIONS = [
  { label: "Röle 1", value: "relay1" },
  { label: "Röle 2", value: "relay2" },
  { label: "Her İkisi", value: "relay12" },
];

const getEntryTypeLabel = (value) => {
  const option = ENTRY_TYPE_OPTIONS.find((opt) => opt.value === value);
  return option ? option.label : value || "-";
};

const getRelayLabel = (value) => {
  const option = RELAY_OPTIONS.find((opt) => opt.value === value);
  return option ? option.label : "Röle 1";
};

const INPUT_PIN_OPTIONS = [
  { label: "Pin 1", value: "pin1" },
  { label: "Pin 2", value: "pin2" },
];

const Devices = () => {
  useTitle("Rovion MES - Cihazlar");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/devices",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    deviceId: null,
  });
  const [formData, setFormData] = useState({
    name: "",
    hardwareId: "",
    entryType: DEFAULT_ENTRY_TYPE,
    inputPin: "",
    notes: "",
    relays: "relay1",
  });

  const headers = [
    {
      name: "Ad",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      name: "Hardware ID",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.hardwareId || a.serialNumber || "").localeCompare(
          String(b.hardwareId || b.serialNumber || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Veri Giriş Tipi",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        getEntryTypeLabel(a.entryType).localeCompare(
          getEntryTypeLabel(b.entryType),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Röle",
      dataType: "text",
      id: 3,
      customSorter: (a, b) =>
        getRelayLabel(a.relays).localeCompare(
          getRelayLabel(b.relays),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Notlar",
      dataType: "text",
      id: 4,
      customSorter: (a, b) =>
        String(a.notes || "").localeCompare(String(b.notes || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      name: "Oluşturulma",
      dataType: "date",
      id: 5,
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    { name: "İşlemler", id: 6, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("devices-list", headers);

  const addToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3000,
    );
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/hardware-devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.hardwareDevices || response.data;
      setDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      addToast("Cihazlar yüklenemedi", "danger");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingDevice(null);
    setFormData({
      name: "",
      hardwareId: "",
      entryType: DEFAULT_ENTRY_TYPE,
      inputPin: "",
      notes: "",
      relays: "relay1",
    });
  };

  const handleCreate = () => {
    resetForm();
    setSidebarOpened(true);
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      name: device.name || "",
      hardwareId: device.hardwareId || device.serialNumber || "",
      entryType: device.entryType || DEFAULT_ENTRY_TYPE,
      inputPin: device.inputPin || "",
      notes: device.notes || "",
      relays: device.relays || "relay1",
    });
    setSidebarOpened(true);
  };

  const handleClose = () => {
    setSidebarOpened(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      addToast("Ad zorunludur", "warning");
      return;
    }
    if (!formData.hardwareId.trim()) {
      addToast("Hardware ID zorunludur", "warning");
      return;
    }
    if (!String(formData.entryType || "").trim()) {
      addToast("Veri Giriş Tipi zorunludur", "warning");
      return;
    }
    try {
      const token = getToken();
      const payload = {
        hardwareId: formData.hardwareId.trim(),
        name: formData.name.trim(),
        entryType: String(formData.entryType || "").trim(),
        relays: formData.relays,
        notes: formData.notes ?? "",
        ...(String(formData.entryType || "").trim() === "input"
          ? { inputPin: formData.inputPin || null }
          : {}),
      };

      if (editingDevice) {
        await axios.put(
          `${API_URL}/api/hardware-devices/${editingDevice._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        addToast("Cihaz başarıyla güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/hardware-devices`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        addToast("Cihaz başarıyla oluşturuldu", "success");
      }

      setSidebarOpened(false);
      resetForm();
      fetchDevices();
    } catch (error) {
      addToast(error.response?.data?.error || "Cihaz kaydedilemedi", "danger");
    }
  };

  const confirmDelete = async () => {
    if (!confirmModal.deviceId) return;
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/hardware-devices/${confirmModal.deviceId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      addToast("Cihaz başarıyla silindi", "success");
      setConfirmModal({ isOpen: false, deviceId: null });
      fetchDevices();
    } catch (error) {
      addToast(error.response?.data?.error || "Cihaz silinemedi", "danger");
      setConfirmModal({ isOpen: false, deviceId: null });
    }
  };

  const formatDate = (d) => formatInstantDateShortTr(d);

  return (
    <AppPage
      title="Cihazlar"
      subtitle="Cihaz kayıtlarını görüntüleyin ve yönetin"
      icon={<FiHardDrive />}
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            onClick={handleCreate}
            icon={<FiPlus />}
          >
            Yeni Cihaz
          </Button>
        ) : undefined
      }
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiHardDrive className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="devices-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {devices.map((device) => (
                <Tr key={device._id} id={device._id} data={device}>
                  {visibleColumns.includes(0) && (
                    <Td>
                      <strong>{device.name || "-"}</strong>
                    </Td>
                  )}
                  {visibleColumns.includes(1) && (
                    <Td>{device.hardwareId || device.serialNumber || "-"}</Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>{getEntryTypeLabel(device.entryType)}</Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>{device.relays ? getRelayLabel(device.relays) : "Röle Seçiniz"}</Td>
                  )}
                  {visibleColumns.includes(4) && <Td>{device.notes || "-"}</Td>}
                  {visibleColumns.includes(5) && (
                    <Td>{formatDate(device.createdAt)}</Td>
                  )}
                  {visibleColumns.includes(6) && (
                    <Td>
                      <div className="d-flex align-items-center gap-1">
                        {canWrite && (
                          <>
                            <IconButton
                              icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() => handleEdit(device)}
                            />
                            <IconButton
                              icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                              appearance="subtle"
                              shape="circle"
                              onClick={() =>
                                setConfirmModal({
                                  isOpen: true,
                                  deviceId: device._id,
                                })
                              }
                            />
                          </>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>

            {devices.length === 0 && (
              <div className="empty-state">
                <FiHardDrive size={64} />
                <p>Cihaz bulunamadı</p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingDevice ? "Cihazı Düzenle" : "Yeni Cihaz Ekle"}
        onClose={handleClose}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div>
            <div className="mt-2">
              <Label className="mb-0">Ad *</Label>
              <Input
                type="text"
                placeholder="Cihaz adı"
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
              />
            </div>

            <div className="mt-2">
              <Label className="mb-0">Hardware ID *</Label>
              <Input
                type="text"
                placeholder="Donanım kimliği (MQTT / cihaz id)"
                value={formData.hardwareId}
                onChange={(value) =>
                  setFormData({ ...formData, hardwareId: value })
                }
              />
            </div>

            <div className="mt-2">
              <Select
                label="Veri Giriş Tipi"
                required
                options={ENTRY_TYPE_OPTIONS}
                selected={
                  formData.entryType
                    ? [formData.entryType]
                    : [DEFAULT_ENTRY_TYPE]
                }
                onSelected={(values) =>
                  setFormData({
                    ...formData,
                    entryType: values?.[0]?.value || DEFAULT_ENTRY_TYPE,
                    // Clear pin when switching away from input
                    inputPin: values?.[0]?.value === "input" ? formData.inputPin : "",
                  })
                }
                unique
                placeholder="Seçiniz"
              />
            </div>

            {/* Pin selector — only visible when entryType is 'input' */}
            {formData.entryType === "input" && (
              <div className="mt-2">
                <Select
                  label="Pin Seçimi"
                  options={INPUT_PIN_OPTIONS}
                  selected={formData.inputPin ? [formData.inputPin] : []}
                  onSelected={(values) =>
                    setFormData({
                      ...formData,
                      inputPin: values?.[0]?.value || "",
                    })
                  }
                  unique
                  placeholder="Pin seçiniz (opsiyonel)"
                />
                <small style={{ color: "#6b7280", fontSize: "0.78rem", display: "block", marginTop: 4 }}>
                  Seçili pindeki sinyal okunur. Seçilmezse her iki pindeki ilk sinyal kullanılır.
                </small>
              </div>
            )}

            <div className="mt-2">
              <Select
                label="Röle Seçimi"
                required
                options={RELAY_OPTIONS}
                selected={formData.relays ? [formData.relays] : ["relay1"]}
                onSelected={(values) =>
                  setFormData({
                    ...formData,
                    relays: values?.[0]?.value || "relay1",
                  })
                }
                unique
                placeholder="Seçiniz"
              />
            </div>

            <div className="form-group mb-3 mt-2">
              <Label className="mb-0">Notlar (opsiyonel)</Label>
              <textarea
                className="form-control"
                placeholder="Ek notlar (opsiyonel)"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>
          </div>

          <div className="mt-4 d-flex gap-2 justify-content-end">
            <Button appearance="danger-subtle" onClick={handleClose}>
              İptal
            </Button>
            <Button appearance="primary-animated" onClick={handleSubmit}>
              {editingDevice ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, deviceId: null })}
        width="small"
      >
        <ModalHeader>Cihazı Sil</ModalHeader>
        <ModalBody>
          <p>
            Bu cihazı silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="subtle"
            onClick={() => setConfirmModal({ isOpen: false, deviceId: null })}
          >
            İptal
          </Button>
          <Button appearance="danger" onClick={confirmDelete}>
            Sil
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(Devices, ["admin", "employee", "user"]);
